import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { PowerProfileDataPoint } from 'greener-ui-library';
import { useSnackbar } from 'notistack';
import { parsePowerProfileCsv } from 'pages/Blueprints/utils/parsePowerProfileCsv';
import { LoadProfile } from 'types';
import { keysToCamelCase, truncateText } from 'utils/formatters';
import { useAxios } from './useAxios';

export interface PowerProfileValidationResponse {
  success: boolean;
  data?: PowerProfileDataPoint[];
  errors?: string[];
  warnings?: string[];
  message?: string;
  processedFileStatistics?: Partial<LoadProfile>;
}

export const useValidatePowerProfileFile = () => {
  const { axiosRequest } = useAxios();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const MAX_ATTEMPTS = 30;

  const getProcessedFileDownloadUrl = async (jobId: string) => {
    let attempt = 0;

    while (attempt <= MAX_ATTEMPTS) {
      const jobResultResponse = await axiosRequest({
        method: 'GET',
        path: `/blueprints/power-profile/process-jobs/${jobId}/`,
      });

      const jobResultData = jobResultResponse?.data;
      const status = jobResultData?.status;
      const processedFileDownloadUrl = jobResultData?.processed_file_download_url;
      const processedFileStatistics = jobResultData?.processed_file_statistics;

      if (status === 'complete' && jobResultData?.error) {
        throw new Error(jobResultData?.error);
      }

      if (status === 'complete' && processedFileDownloadUrl) {
        return {
          processedFileDownloadUrl,
          processedFileStatistics: keysToCamelCase(processedFileStatistics),
        };
      }

      attempt += 1;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    throw new Error(t('blueprints:wizard.fields.powerProfile.validationTimeout'));
  };

  const validateFile = async (file: File): Promise<PowerProfileValidationResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const createJobResponse = await axiosRequest({
        method: 'POST' as const,
        path: '/blueprints/power-profile/process-jobs/',
        body: formData,
      });

      const jobId = createJobResponse?.data?.job_id;

      if (!jobId) {
        throw new Error();
      }

      const { processedFileDownloadUrl, processedFileStatistics } = await getProcessedFileDownloadUrl(jobId);

      const downloadResponse = await axios.get(processedFileDownloadUrl);

      const csvData = downloadResponse.data as string;
      const dataPoints = parsePowerProfileCsv(csvData);

      return {
        success: true,
        data: dataPoints,
        message: 'File validated successfully',
        processedFileStatistics,
      };
    } catch (error: unknown) {
      console.error('Power profile file validation failed:', error);

      // Handle errors from backend
      let errorMessage = 'File validation failed';
      let errors: string[] = [];

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown } };
        if (axiosError.response?.data) {
          const responseData = axiosError.response.data;
          // If backend returns structured errors
          if (
            responseData &&
            typeof responseData === 'object' &&
            'errors' in responseData &&
            Array.isArray(responseData.errors)
          ) {
            errors = responseData.errors as string[];
          } else if (responseData && typeof responseData === 'object' && 'detail' in responseData) {
            // Backend can return detail as string or array of strings
            if (typeof responseData.detail === 'string') {
              errorMessage = responseData.detail;
            } else if (Array.isArray(responseData.detail)) {
              // Join multiple error messages with newlines
              errorMessage = responseData.detail.join('\n');
            }
          } else if (
            responseData &&
            typeof responseData === 'object' &&
            'message' in responseData &&
            typeof responseData.message === 'string'
          ) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          }
        }
      } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      errorMessage = truncateText({ text: errorMessage });

      enqueueSnackbar(
        t('blueprints:wizard.fields.powerProfile.validationError', {
          error: errorMessage,
        }),
        { variant: 'error' }
      );

      return {
        success: false,
        errors: errors.length > 0 ? errors : [errorMessage],
        message: errorMessage,
      };
    }
  };

  return { validateFile };
};
